import { formatDistanceToNowStrict } from 'date-fns';
import { AlertTriangle, CheckCircle2, Globe, Layers, Server, XCircle } from 'lucide-react';
import type { RunSummary, ScheduleTarget } from '../../types/scheduledCommands';
import type { ScheduleState } from '../../utils/scheduledCommands';
import { targetKey, targetLabel } from '../../utils/scheduledCommands';
import { formatReleaseAt } from '../../utils/comingSoon';
import { cn } from '../../utils/cn';

/** "in 4 minutes" / "3 hours ago", with the exact time one hover away. */
export const RelativeTime = ({ at, className }: { at: number; className?: string }) => (
  <time
    dateTime={new Date(at * 1000).toISOString()}
    title={formatReleaseAt(at)}
    className={cn('tabular-nums', className)}
  >
    {formatDistanceToNowStrict(new Date(at * 1000), { addSuffix: true })}
  </time>
);

const STATE_STYLE: Record<ScheduleState, { label: string; tone: string }> = {
  active: { label: 'Active', tone: 'badge-success' },
  paused: { label: 'Paused', tone: 'badge-neutral' },
  ended: { label: 'Ended', tone: 'badge-neutral' },
  not_started: { label: 'Not started', tone: 'badge-info' },
};

export const ScheduleStateBadge = ({ state, className }: { state: ScheduleState; className?: string }) => (
  <span className={cn('badge badge-dot', STATE_STYLE[state].tone, className)}>{STATE_STYLE[state].label}</span>
);

const TargetIcon = ({ target }: { target: ScheduleTarget }) => {
  const Icon = target.type === 'all' ? Globe : target.type === 'cluster' ? Layers : Server;
  return <Icon className="w-3 h-3 shrink-0" />;
};

/** A schedule's targets in the space of a table cell: the first few, then a count. */
export const TargetChips = ({ targets, max = 2, className }: { targets: ScheduleTarget[]; max?: number; className?: string }) => {
  const shown = targets.slice(0, max);
  const rest = targets.slice(max);
  return (
    <span className={cn('flex flex-wrap items-center gap-1', className)}>
      {shown.map((target) => (
        <span key={targetKey(target)} className="chip max-w-[14rem]">
          <TargetIcon target={target} />
          <span className={cn('truncate', target.type === 'server' && 'font-mono')}>{targetLabel(target)}</span>
        </span>
      ))}
      {rest.length > 0 && (
        <span className="text-xs text-glass-400 tabular-nums" title={rest.map(targetLabel).join(', ')}>
          +{rest.length} more
        </span>
      )}
    </span>
  );
};

const RUN_TONE: Record<RunSummary['status'], string> = {
  ok: 'text-green-400',
  partial: 'text-amber-400',
  failed: 'text-red-400',
  error: 'text-red-400',
};

export const RunStatusIcon = ({ run, className }: { run: RunSummary; className?: string }) => {
  const Icon = run.status === 'ok' ? CheckCircle2 : run.status === 'partial' ? AlertTriangle : XCircle;
  return <Icon className={cn('w-4 h-4 shrink-0', RUN_TONE[run.status], className)} aria-hidden="true" />;
};

/** "12/12", "10/12", or why a run broke. */
export function runOutcome(run: RunSummary): string {
  if (run.status === 'error') return run.error ? `Failed: ${run.error}` : 'Failed';
  return `${run.responded_count}/${run.expected_count}`;
}

export function runTitle(run: RunSummary): string {
  const parts = [`${run.responded_count} of ${run.expected_count} servers ran it`];
  if (run.failed.length) parts.push(`Did not run on ${run.failed.join(', ')}`);
  if (run.error) parts.push(run.error);
  return parts.join('. ');
}

export const LastRun = ({ run }: { run: RunSummary | null }) => {
  if (!run) return <span className="text-xs text-glass-500">Never run</span>;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs" title={runTitle(run)}>
      <RunStatusIcon run={run} className="w-3.5 h-3.5" />
      <span className={cn('font-mono tabular-nums', RUN_TONE[run.status])}>
        {run.status === 'error' ? 'Error' : runOutcome(run)}
      </span>
      <RelativeTime at={run.started_at} className="text-glass-500" />
    </span>
  );
};

export const TRIGGER_LABEL: Record<RunSummary['trigger'], string> = {
  schedule: 'On schedule',
  manual: 'Run now',
  player: 'Sent to a player',
};
